import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const MODULES = [
  {
    id: 'dashboard',
    icon: '📊',
    name: 'Dashboard',
    path: '/dashboard',
    role: 'All users',
    desc: 'Central KPI view — live counts for active alerts, average OTD %, cost savings, and supplier risk distribution.',
    steps: [
      'Login with your credentials — you land on the Dashboard automatically.',
      'Review the 4 KPI cards: Active Alerts, Average OTD, Cost Savings YTD, At-Risk Suppliers.',
      'Cards auto-refresh. Click any card to jump to that module.',
      'Colour coding: Red = action needed, Amber = watch, Green = healthy.',
    ],
    voice: ['"Go to dashboard"'],
  },
  {
    id: 'suppliers',
    icon: '🏭',
    name: 'Supplier Scorecard',
    path: '/suppliers',
    role: 'All users',
    desc: 'AI-powered supplier risk scorecard. IsolationForest ML scores each supplier from -1 (anomalous) to +1 (healthy). OTD and quality scores drive tier classification: Gold ≥ 85%, Silver ≥ 70%, Approved ≥ 50%, Probation < 50%.',
    steps: [
      'Click a supplier row to expand and see full OTD history, quality score, and Claude AI risk explanation.',
      'Use the tier filter buttons (Gold / Silver / Approved / Probation / At-Risk) to narrow the list.',
      'Click "Add Delivery" to log a new delivery event — this recalculates the OTD score.',
      'Click "Recalculate Tiers" to force a fresh ML scoring run across all suppliers.',
      'At-risk suppliers (below 50% OTD) are highlighted in red and shown on the Dashboard.',
    ],
    voice: ['"Show suppliers"', '"Show at-risk suppliers"'],
  },
  {
    id: 'alerts',
    icon: '🚨',
    name: 'Alerts',
    path: '/alerts',
    role: 'All users',
    desc: 'Active supply chain risk alerts generated by the AI anomaly engine and manual triggers. Each alert has a severity (Critical / Warning / Info) and a Claude AI-written natural language explanation.',
    steps: [
      'Filter by severity using the CRITICAL / WARNING / INFO buttons.',
      'Read the AI-generated explanation below each alert title.',
      'Click "Dismiss" on an alert once the issue is resolved or acknowledged.',
      'Click "Dismiss All" to clear all visible alerts at once.',
      'Dismissed alerts are removed immediately — demo data resets daily at midnight IST.',
    ],
    voice: ['"Open alerts"', '"How many alerts"', '"Dismiss alert"', '"Dismiss all alerts"'],
  },
  {
    id: 'bom',
    icon: '📋',
    name: 'Bill of Materials',
    path: '/bom',
    role: 'All users',
    desc: 'Product BOM registry. Each BOM links to an item number and tracks approval status. BOM components include nested item references.',
    steps: [
      'Use the search bar to find BOMs by name or external ID.',
      'Click "View Details" or any row to open the BOM detail page with full component tree.',
      'Click "+ Create BOM" to create a new BOM — enter name, item number, and optional external ID.',
      'Newly created BOMs start in DRAFT status until manually approved.',
    ],
    voice: ['"Go to BOM"', '"Create BOM [name]"  e.g. "Create BOM Laptop Assembly"', '"How many BOMs"'],
  },
  {
    id: 'cost-records',
    icon: '💰',
    name: 'Cost Records',
    path: '/cost-records',
    role: 'All users (approve = Admin/Manager)',
    desc: 'Cost change management workflow: DRAFT → PENDING_APPROVAL → APPROVED or REJECTED. Approved records automatically update the Item Master with the new cost.',
    steps: [
      'Click "+ New Cost Record" to create a draft — enter item key, proposed cost, effective date, and justification (min 10 characters).',
      'DRAFT records can be edited or submitted. Click "Submit" to move to PENDING_APPROVAL.',
      'Admins/Managers see "Approve" and "Reject" buttons on PENDING records.',
      'Approving a record updates the Item Master cost automatically.',
      'Filter the list by status: ALL / DRAFT / PENDING_APPROVAL / APPROVED / REJECTED.',
      'Use the search bar to find records by item code.',
    ],
    voice: ['"Open cost records"', '"Create cost record for [ITEM-CODE]"  e.g. "Create cost record for CHIP-001"', '"Submit cost record"', '"Approve cost record"'],
  },
  {
    id: 'reports',
    icon: '📈',
    name: 'Management Reports',
    path: '/reports',
    role: 'All users',
    desc: 'Executive analytics across 4 report types with CSV export and browser Print/PDF on every tab.',
    steps: [
      'Supplier Performance — bar chart + sparkline trend for OTD, Quality, and Composite score per supplier. Toggle metrics using the 3 buttons at the top.',
      'Cost Variance — budget vs actual table with over/under status and KPI summary cards.',
      'Alert Summary — SVG donut chart by alert type + monthly stacked bar volume chart.',
      'User Activity — full audit log of all actions taken in the platform.',
      'Click "Export CSV" on any tab to download the data as a spreadsheet.',
      'Click "Print / PDF" to open the browser print dialog — select "Save as PDF" to get a report.',
    ],
    voice: ['"Show reports"'],
  },
  {
    id: 'forecasting',
    icon: '🔮',
    name: 'Forecasting',
    path: '/forecasts',
    role: 'All users',
    desc: 'Demand forecasting module. Create and view quantity forecasts per item for future planning periods.',
    steps: [
      'Click "+ New Forecast" to create a forecast — enter item key, start period, end period, and quantity.',
      'View existing forecasts in the list — click any row to expand the detail.',
      'Use "Variance Report" to compare actual vs forecast across items.',
    ],
    voice: ['"Open forecasting"'],
  },
  {
    id: 'ai',
    icon: '🤖',
    name: 'AI Anomaly Engine',
    path: '/ai',
    role: 'All users',
    desc: 'IsolationForest ML engine results + LangGraph agent pipeline output. Shows anomaly scores, risk classifications, and Claude AI natural language recommendations per supplier.',
    steps: [
      'View the list of supplier anomaly detections — each shows confidence score and risk classification.',
      'Click any anomaly to read the Claude AI recommendation in plain English.',
      'The engine runs automatically — scores update when deliveries are added.',
      'LangGraph agent runs a 5-node pipeline: fetch → score → explain → validate → log.',
    ],
    voice: ['"Open AI engines"'],
  },
  {
    id: 'users',
    icon: '👥',
    name: 'User Management',
    path: '/admin/users',
    role: 'Admin only',
    desc: 'Create, update, and disable user accounts. Assign roles: ADMIN (full access), MANAGER (approve cost records), GUEST/VIEWER (read-only).',
    steps: [
      'Click "+ Create User" to add a new account — enter user ID, name, email, role, and initial password.',
      'Click "Edit" on any user row to update name, email, or role.',
      'Click "Set Password" to reset a user\'s password.',
      'Click "Disable" to deactivate an account — the user cannot log in until re-enabled.',
      'Roles control what each user can see and do across all modules.',
    ],
    voice: ['"Show users"', '"Create user [name]"  e.g. "Create user John Manager"'],
  },
  {
    id: 'tests',
    icon: '🧪',
    name: 'Test Dashboard',
    path: '/tests',
    role: 'All users',
    desc: 'Live Playwright test results — 51 tests across 9 modules, all passing. Auto-refreshes every 30 seconds. Shows pass/fail counts, API health checks for all 14 endpoints, and known bug tracker.',
    steps: [
      'Results tab — see the full 51-test breakdown with pass/fail per test case.',
      'API Health tab — live ping of all 14 REST endpoints with response time.',
      'Known Bugs tab — documented bugs found through testing with severity and fix status.',
      'Click "Run All Checks" to force a fresh API health check.',
      'In demo mode, all 51 tests show as passing (static mock data — no live backend).',
    ],
    voice: ['"Open test dashboard"'],
  },
  {
    id: 'eval',
    icon: '🎯',
    name: 'Eval Dashboard',
    path: '/eval',
    role: 'All users',
    desc: 'LLM evaluation harness results. Measures Claude AI prompt consistency (94.2%), hallucination rate, and answer relevancy across 5 test cases using deepeval.',
    steps: [
      'View the evaluation summary — overall score, consistency rate, hallucination rate.',
      'Click any eval row to see the individual prompt, expected answer, and Claude\'s actual response.',
      'Click "Run Evaluation" to trigger a fresh eval run (requires AI service to be running).',
      'In demo mode, displays pre-recorded scores from the last real evaluation run.',
    ],
    voice: ['"Open eval"'],
  },
  {
    id: 'setup',
    icon: '⚙️',
    name: 'Setup Wizard',
    path: '/setup',
    role: 'Admin',
    desc: 'First-time platform configuration — 5 steps: Company Profile → Supplier Setup → Item Categories → User Accounts → Launch.',
    steps: [
      'Step 1 — Company Profile: Enter organisation name, industry, currency, and timezone.',
      'Step 2 — Supplier Setup: Add initial suppliers with name and country.',
      'Step 3 — Item Categories: Select the item types your organisation works with.',
      'Step 4 — User Accounts: Set the administrator user ID and password.',
      'Step 5 — Launch: Review configuration summary and click "Launch Platform".',
      'Click "Skip Setup" on step 1 to go directly to the Dashboard without configuring.',
    ],
    voice: ['"Open setup"'],
  },
];

const VOICE_REF = [
  { category: 'Navigate to any module', color: '#a9790f', commands: [
    { cmd: '"Go to dashboard"',          what: 'Opens Dashboard' },
    { cmd: '"Show suppliers"',           what: 'Opens Supplier Scorecard' },
    { cmd: '"Open alerts"',              what: 'Opens Alerts' },
    { cmd: '"Go to BOM"',                what: 'Opens Bill of Materials' },
    { cmd: '"Open cost records"',        what: 'Opens Cost Records' },
    { cmd: '"Show reports"',             what: 'Opens Management Reports' },
    { cmd: '"Show users"',               what: 'Opens User Management' },
    { cmd: '"Open forecasting"',         what: 'Opens Forecasting' },
    { cmd: '"Open AI engines"',          what: 'Opens AI Anomaly Engine' },
    { cmd: '"Open eval"',                what: 'Opens Eval Dashboard' },
    { cmd: '"Open test dashboard"',      what: 'Opens Test Dashboard' },
    { cmd: '"Open setup"',               what: 'Opens Setup Wizard' },
  ]},
  { category: 'Create records (opens pre-filled form)', color: '#0891b2', commands: [
    { cmd: '"Create BOM [name]"',                       what: 'Navigates to BOM → opens create modal with name pre-filled. e.g. "Create BOM Laptop Assembly"' },
    { cmd: '"Create cost record for [ITEM-CODE]"',      what: 'Navigates to Cost Records → opens create modal with item key pre-filled. e.g. "Create cost record for CHIP-001"' },
    { cmd: '"Create user [name]"',                      what: 'Navigates to User Management → opens create modal with name pre-filled. e.g. "Create user John Manager"' },
  ]},
  { category: 'Take actions (same as clicking buttons)', color: '#d97706', commands: [
    { cmd: '"Dismiss alert"',            what: 'Dismisses the first active alert via API — same as clicking the Dismiss button' },
    { cmd: '"Dismiss all alerts"',       what: 'Dismisses every active alert in one go' },
    { cmd: '"Submit cost record"',       what: 'Submits the first DRAFT cost record for approval' },
    { cmd: '"Approve cost record"',      what: 'Approves the first PENDING_APPROVAL cost record' },
    { cmd: '"Logout"',                   what: 'Clears session and returns to login page' },
  ]},
  { category: 'Query supply chain data', color: '#16a34a', commands: [
    { cmd: '"How many alerts"',          what: 'Speaks the current active alert count' },
    { cmd: '"OTD score"',                what: 'Speaks the average on-time delivery percentage across all suppliers' },
    { cmd: '"At-risk suppliers"',        what: 'Speaks the names of at-risk suppliers and opens the scorecard' },
    { cmd: '"Cost savings"',             what: 'Speaks the current quarter cost savings figure' },
    { cmd: '"How many BOMs"',            what: 'Speaks the total BOM count' },
    { cmd: '"Help"',                     what: 'Speaks a summary of available commands' },
  ]},
];

function ModuleCard({ mod, onNav }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden', marginBottom:12 }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', cursor:'pointer', userSelect:'none',
          background: expanded ? '#f8fafc' : '#fff', borderBottom: expanded ? '1px solid #f1f5f9' : 'none' }}
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background='#f8fafc'; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background='#fff'; }}>
        <span style={{ fontSize:22, flexShrink:0 }}>{mod.icon}</span>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:800, fontSize:14, color:'#1e293b' }}>{mod.name}</div>
          <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{mod.desc}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <span style={{ fontSize:10, background:'#f1f5f9', color:'#6b7280', padding:'2px 8px', borderRadius:8, fontWeight:600, whiteSpace:'nowrap' }}>{mod.role}</span>
          <span style={{ fontSize:11, color:'#9ca3af', transition:'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none' }}>▼</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding:'16px 18px' }}>
          <div style={{ display:'grid', gridTemplateColumns: mod.voice.length ? '1fr 260px' : '1fr', gap:20 }}>
            {/* Steps */}
            <div>
              <div style={{ fontSize:11, fontWeight:800, color:'#374151', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:10 }}>
                How to use
              </div>
              <ol style={{ margin:0, paddingLeft:18 }}>
                {mod.steps.map((s, i) => (
                  <li key={i} style={{ fontSize:12, color:'#374151', marginBottom:7, lineHeight:1.6 }}>{s}</li>
                ))}
              </ol>
            </div>

            {/* Voice commands + navigate */}
            <div>
              {mod.voice.length > 0 && (
                <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'12px 14px', marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:800, color:'#a9790f', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:8 }}>
                    🎤 Voice Commands
                  </div>
                  {mod.voice.map(v => (
                    <div key={v} style={{ fontSize:11, color:'#1e40af', fontFamily:'monospace', marginBottom:5, padding:'3px 0', borderBottom:'1px dashed #dbeafe' }}>{v}</div>
                  ))}
                </div>
              )}
              <button onClick={() => onNav(mod.path)}
                style={{ width:'100%', padding:'9px 0', background:'#a9790f', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>
                Open {mod.name} →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VoiceRef() {
  return (
    <div>
      {VOICE_REF.map(cat => (
        <div key={cat.category} style={{ marginBottom:22 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <div style={{ width:4, height:18, background:cat.color, borderRadius:2 }} />
            <div style={{ fontSize:13, fontWeight:800, color:cat.color }}>{cat.category}</div>
          </div>
          <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, overflow:'hidden' }}>
            {cat.commands.map((c, i) => (
              <div key={c.cmd} style={{ display:'grid', gridTemplateColumns:'240px 1fr', padding:'10px 14px',
                borderBottom: i < cat.commands.length - 1 ? '1px solid #f1f5f9' : 'none',
                background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                <div style={{ fontFamily:'monospace', fontSize:12, fontWeight:700, color: cat.color, paddingRight:12 }}>{c.cmd}</div>
                <div style={{ fontSize:12, color:'#374151', lineHeight:1.5 }}>{c.what}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'12px 16px', fontSize:12, color:'#92400e' }}>
        <strong>Note:</strong> Voice recognition requires Chrome or Edge browser. Click the <strong>"🎤 Voice Commands"</strong> label at the top of any page to open the voice panel. You can also click the chip buttons inside the panel to run commands without speaking.
      </div>
    </div>
  );
}

const TABS = ['All Modules', 'Voice Commands', 'Demo Guide', 'Security'];

export default function HelpPage() {
  const [tab,    setTab]    = useState(0);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const filtered = search
    ? MODULES.filter(m => m.name.toLowerCase().includes(search.toLowerCase()) || m.desc.toLowerCase().includes(search.toLowerCase()))
    : MODULES;

  return (
    <div style={{ padding:24, maxWidth:1000, margin:'0 auto' }}>
      {/* Page header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
          <span style={{ fontSize:28 }}>📖</span>
          <h1 style={{ fontSize:24, fontWeight:800, margin:0 }}>Help &amp; User Guide</h1>
        </div>
        <p style={{ color:'#6b7280', margin:0, fontSize:13 }}>
          Complete guide to the Supply Chain Intelligence Platform — modules, voice commands, and how to use every feature
        </p>
      </div>

      {/* Quick action cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
        {[
          { label:'Demo Login',    val:'kumar / Kumar@2026', icon:'🔑', color:'#a9790f', bg:'#eff6ff' },
          { label:'Total Modules', val:'12 modules',          icon:'📦', color:'#0891b2', bg:'#ecfeff' },
          { label:'Voice Commands',val:'20+ commands',        icon:'🎤', color:'#d97706', bg:'#fffbeb' },
          { label:'Test Suite',    val:'51 / 51 passing',     icon:'✅', color:'#16a34a', bg:'#f0fdf4' },
        ].map(c => (
          <div key={c.label} style={{ background:c.bg, border:`1px solid ${c.color}30`, borderRadius:10, padding:'14px 16px' }}>
            <div style={{ fontSize:20, marginBottom:6 }}>{c.icon}</div>
            <div style={{ fontSize:18, fontWeight:800, color:c.color }}>{c.val}</div>
            <div style={{ fontSize:11, color:c.color, opacity:0.8, marginTop:2, fontWeight:600 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid #e2e8f0', marginBottom:20 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            style={{ padding:'9px 18px', fontSize:13, fontWeight:600, cursor:'pointer', border:'none', background:'none',
              borderBottom: tab===i ? '2px solid #a9790f' : '2px solid transparent',
              color: tab===i ? '#a9790f' : '#94a3b8', marginBottom:-1 }}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab: All Modules */}
      {tab === 0 && (
        <div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search modules…"
            style={{ width:'100%', padding:'9px 14px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', marginBottom:16, boxSizing:'border-box' }} />
          {filtered.map(m => <ModuleCard key={m.id} mod={m} onNav={navigate} />)}
        </div>
      )}

      {/* Tab: Voice Commands */}
      {tab === 1 && (
        <div>
          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'14px 18px', marginBottom:20, display:'flex', gap:12, alignItems:'flex-start' }}>
            <span style={{ fontSize:24, flexShrink:0 }}>🎤</span>
            <div>
              <div style={{ fontWeight:800, fontSize:14, color:'#a9790f', marginBottom:4 }}>How to use Voice Commands</div>
              <div style={{ fontSize:12, color:'#1e40af', lineHeight:1.7 }}>
                Look for the <strong>"🎤 Voice Commands ▾"</strong> pill label at the top-right of every page.<br />
                <strong>Hover</strong> over it to see available commands. <strong>Click</strong> to open the voice panel.<br />
                Then click <strong>"Click to Speak"</strong> and speak your command, or click any suggestion chip.<br />
                Works in <strong>Chrome</strong> and <strong>Edge</strong> only (Web Speech API).
              </div>
            </div>
          </div>
          <VoiceRef />
        </div>
      )}

      {/* Tab: Demo Guide */}
      {tab === 2 && (
        <div style={{ display:'grid', gap:16 }}>
          {[
            { title:'Demo Credentials', icon:'🔑', content: (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:10 }}>
                  {[
                    { user:'kumar',  pass:'Kumar@2026',  role:'Admin — full access to all modules' },
                    { user:'admin',  pass:'Admin@2026',  role:'Admin — full access including user management' },
                    { user:'viewer', pass:'Viewer@2026', role:'Viewer — read-only, no create/approve' },
                  ].map(u => (
                    <div key={u.user} style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:8, padding:12 }}>
                      <div style={{ fontFamily:'monospace', fontWeight:800, color:'#a9790f', fontSize:13 }}>{u.user}</div>
                      <div style={{ fontFamily:'monospace', color:'#374151', fontSize:12, marginTop:2 }}>{u.pass}</div>
                      <div style={{ fontSize:11, color:'#6b7280', marginTop:6, lineHeight:1.5 }}>{u.role}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:12, color:'#6b7280' }}>Demo data automatically resets every day at midnight IST. Create, edit, and delete anything — it all restores overnight.</div>
              </div>
            )},
            { title:'What to Demo — Suggested Flow', icon:'🎯', content: (
              <ol style={{ margin:0, paddingLeft:18 }}>
                {[
                  'Login with kumar / Kumar@2026 — land on the Dashboard and review the 4 KPI cards.',
                  'Go to Suppliers — see the IsolationForest ML risk scores. Click TechParts India to read the Claude AI explanation.',
                  'Go to Alerts — read the AI-written explanations. Dismiss one alert.',
                  'Go to Cost Records — create a new cost record for item CHIP-001. Submit it for approval. Then approve it.',
                  'Go to Reports — check the Supplier Performance tab. Toggle between OTD and Quality. Export CSV.',
                  'Use the Voice Commands pill — try "Create BOM Hydraulic Kit", watch the modal open pre-filled.',
                  'Go to Test Dashboard — show the 51/51 passing badge and 14/14 API health checks.',
                  'Go to AI Engines — show IsolationForest scores and LangGraph pipeline results.',
                  'Go to Eval Dashboard — show the 94.2% LLM consistency rate.',
                ].map((s, i) => <li key={i} style={{ fontSize:13, marginBottom:8, lineHeight:1.6 }}>{s}</li>)}
              </ol>
            )},
            { title:'Manual Data Reset (Admin only)', icon:'🔄', content: (
              <div>
                <p style={{ fontSize:13, color:'#374151', margin:'0 0 10px' }}>If you want to restore all seed data without waiting for midnight:</p>
                <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:8, padding:14, fontFamily:'monospace', fontSize:12, color:'#374151', marginBottom:10 }}>
                  POST /api/admin/reset-demo<br />
                  Authorization: Bearer &lt;admin-token&gt;
                </div>
                <p style={{ fontSize:12, color:'#6b7280', margin:0 }}>Restores: 5 suppliers, 3 alerts, 4 cost records, 3 BOMs, 3 users. Admin role required.</p>
              </div>
            )},
          ].map(s => (
            <div key={s.title} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'18px 20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                <span style={{ fontSize:18 }}>{s.icon}</span>
                <h3 style={{ margin:0, fontSize:15, fontWeight:800 }}>{s.title}</h3>
              </div>
              {s.content}
            </div>
          ))}
        </div>
      )}

      {/* Tab: Security */}
      {tab === 3 && (
        <div style={{ display:'grid', gap:14 }}>
          {[
            { title:'Authentication', items:['JWT tokens with 8-hour expiry', 'BCrypt password hashing (minimum 60-char hash)', 'Null-password protection — P0 auth bypass patched', 'Token validated on every API request'] },
            { title:'Authorisation (RBAC)', items:['ADMIN — full access to all endpoints including user management and demo reset', 'MANAGER — approve/reject cost records, read all modules', 'VIEWER / GUEST — read-only, cannot create, update, or delete'] },
            { title:'Rate Limiting', items:['5 login attempts per minute per IP address (Bucket4j)', '15-minute lockout after 5 consecutive failures', 'Applied to /api/auth/login endpoint only'] },
            { title:'Input & Transport Security', items:['XSS sanitisation on all user inputs', '1 MB request size limit to prevent payload attacks', 'HTTPS only in production (Railway)', 'HSTS, X-Frame-Options, CSP, X-Content-Type-Options headers set'] },
            { title:'Audit & Monitoring', items:['OWASP Top 10 audit across all 10 modules', 'SAST scanning integrated in CI/CD', 'Global exception handler — no stack traces in API responses', 'SQL injection prevention via JPA parameterised queries'] },
          ].map(s => (
            <div key={s.title} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'16px 18px' }}>
              <h3 style={{ margin:'0 0 10px', fontSize:14, fontWeight:800, color:'#1e293b' }}>{s.title}</h3>
              <ul style={{ margin:0, paddingLeft:18 }}>
                {s.items.map((it, i) => <li key={i} style={{ fontSize:12, color:'#374151', marginBottom:6, lineHeight:1.6 }}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
